import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { filter, map } from 'rxjs/operators';
import { APP_CONSTANTS } from '../../constants/app.constants';

@Injectable({
  providedIn: 'root',
})
export class TitleService {
  private router = inject(Router);
  private title = inject(Title);
  private readonly appName = APP_CONSTANTS.TITLE; // Replace with your app's name

  constructor() {
    this.init();
  }

  /**
   * Initializes the title service by listening for route changes and updating the document title accordingly.
   */
  private init(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.collectRouteHierarchy(this.router.routerState.root)),
        map((titles) =>
          titles.map((title) => this.removeTrailingNumbers(title))
        ) // Process titles
      )
      .subscribe((titles) => {
        const fullTitle = titles.length
          ? `${titles.join(' | ')} | ${this.appName}`
          : this.appName;
        this.title.setTitle(fullTitle);
      });
  }

  /**
   * Recursively collects the full route hierarchy, including dynamic segments.
   * @param {ActivatedRoute} route - The current route.
   * @param {string[]} titles - The list of collected titles.
   * @returns {string[]} - The array of collected and formatted route titles.
   */
  /**
   * Recursively collects the full route hierarchy, ensuring no duplicate titles.
   * @param {ActivatedRoute} route - The current route.
   * @param {string[]} titles - The list of collected titles.
   * @returns {string[]} - The array of collected and formatted route titles.
   */
  private collectRouteHierarchy(
    route: ActivatedRoute,
    titles: string[] = []
  ): string[] {
    let newTitle = '';

    if (route.snapshot.data['title']) {
      newTitle = this.formatTitle(route.snapshot.data['title']);
    } else if (route.snapshot.url.length) {
      // Extract and format the last segment (e.g., "file-details/2" → "File Details")
      const urlSegment = route.snapshot.url
        .map((segment) => this.formatTitle(segment.path))
        .join('/');
      newTitle = this.removeTrailingNumbers(urlSegment); // Ensure numbers like "/2" are removed
    }

    // Prevent duplicate consecutive titles
    if (newTitle && titles[titles.length - 1] !== newTitle) {
      titles.push(newTitle);
    }

    // Recursively traverse child routes
    if (route.firstChild) {
      return this.collectRouteHierarchy(route.firstChild, titles);
    }

    return titles;
  }

  /**
   * Formats a title by replacing hyphens with spaces and capitalizing each word.
   * @param {string} title - The title string to be formatted.
   * @returns {string} - The formatted title string.
   */
  private formatTitle(title: string): string {
    return title
      .split('-') // Replace hyphens with spaces
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' ');
  }

  /**
   * Removes trailing numbers after a '/' in the title.
   * @param {string} text - The input title string.
   * @returns {string} - The title string without trailing numbers.
   */
  private removeTrailingNumbers(text: string): string {
    return text.replace(/\/\d+$/, '');
  }
}
